import { ChevronRight } from 'lucide-react';
import { Avatar, Card, Typography } from '../ui';
import { contactDisplayName, contactTurn, shortRelativeTime, longRelativeTime, singleOutreachId } from '../../../lib/contact-rows';
import { TurnBadge } from './TurnBadge';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import type { ContactId, WebContact } from '../../../lib/contact-book';
import { useTranslation } from '../../../lib/i18n';
import { useCategoryRowClass } from '../category-view/CategoryView';
import styles from './contacts.module.css';

/**
 * One person in the Outreach list. Shared 1:1 with iOS `ContactRowView` and Android `ContactRow`.
 *
 * Two per-viewport anatomies, both straight from Figma (web `17937:268706`, native `17937:264687`):
 *  - **desktop** — avatar · [name / "Last update: {when}"] · the turn badge far-end. No chevron.
 *    Card `lg` (20 radius / 20 padding).
 *  - **compact** (mobile-web + the native phones) — avatar · [badge-above-name when there is a badge,
 *    name alone otherwise] · short time (`2d`) + a chevron. Card `sm`+sunken (16 radius / 12·16).
 *    The avatar is always vertically centred; the trailing time+chevron align to the BADGE (top) when
 *    there is one, and to the NAME (centre) when there isn't.
 */
export function ContactRow({
  entry,
  onOpen,
}: {
  entry: WebContact;
  /** Opens the contact by its identity — a person, or (address-only, #10660) a recipient. `outreachId`
   *  is the contact's ONE conversation, to open straight in the viewer; otherwise undefined and the
   *  caller opens the contact screen to choose from several. */
  onOpen: (id: ContactId, outreachId?: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const rowSurface = useCategoryRowClass();
  const card = entry.contact;
  const personId = card?.personId ?? '';
  // A counterparty the people book knows only by an address has no person id — it opens by recipient
  // id instead (#10660), rather than being an inert row.
  const recipientId = entry.recipientId ?? '';
  const contactId: ContactId | null = personId ? { personId } : recipientId ? { recipientId } : null;
  // Whose move it is across all their threads, worst-first — NOT "do they have any open thread",
  // which is what this used to ask and why four rows could wear one badge while only one was true.
  // `undefined` for a contact nobody has messaged: no badge, because never-contacted and finished
  // are different facts.
  const turn = contactTurn(entry);
  const displayName = contactDisplayName(card, entry.channels, t('contacts.row.unnamed'));

  const open = () => {
    if (!contactId) return;
    // person vs address-only recipient (#10660), so recipientId-only opens are distinguishable.
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_contact' },
      { additionalFields: { contact_kind: personId ? 'person' : 'recipient' } },
    );
    // A contact with exactly ONE conversation opens it in the viewer — the contact screen would
    // otherwise be a list of one. More than one has no single answer, so it opens that screen.
    onOpen(contactId, singleOutreachId(entry) ?? undefined);
  };

  const nameEl = (
    <Typography variant="h4" color="primary" className={styles.rowName}>
      {displayName}
    </Typography>
  );
  const badge = turn ? <TurnBadge turn={turn} /> : null;

  return (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      disablePadding={!narrow}
      component="button"
      interactive
      className={`${narrow ? styles.rowCompact : styles.row}${rowSurface ? ` ${rowSurface}` : ''}`}
      onClick={open}
      aria-disabled={!contactId}
    >
      <Avatar
        initials={avatarInitials(displayName)}
        photoUrl={card?.effectiveAvatarUrl}
        size={narrow ? 40 : 32}
        color={avatarSlotFor(personId || recipientId || card?.displayName || '')}
      />

      {narrow ? (
        // Compact: the end cluster's vertical alignment follows the content — top with the badge,
        // centre with the name.
        <span className={badge ? styles.rowRightTop : styles.rowRightCenter}>
          {badge ? (
            <span className={styles.rowMainCompact}>
              {badge}
              {nameEl}
            </span>
          ) : (
            nameEl
          )}
          <span className={styles.rowEnd}>
            <Typography variant="p3" color="secondary" className={styles.rowAge}>
              {shortRelativeTime(entry.lastActivityAt, undefined, t('contacts.time.now'))}
            </Typography>
            <ChevronRight size={24} className="dir-flip" aria-hidden />
          </span>
        </span>
      ) : (
        // Desktop: name over "Last update: …", with the badge at the far end. A person in the book nobody
        // has written to has no last update, so the line is left out rather than reading "Last update:"
        // over nothing — the same no-time rendering both phones give that row.
        <>
          <span className={styles.rowMain}>
            {nameEl}
            {entry.lastActivityAt ? (
              <Typography variant="p3" color="secondary" className={styles.rowSubtitle}>
                {t('contacts.row.lastUpdate', { time: longRelativeTime(entry.lastActivityAt) })}
              </Typography>
            ) : null}
          </span>
          {badge}
        </>
      )}
    </Card>
  );
}
